import { wait } from '@/package';

import type {
  IVStorage,
  S3ObjectInfo,
  VStorageConfig,
  VStorageGetCommandParameter,
  VStorageListCommandParameter,
  VStorageProgress,
  VStoragePutCommandParameter,
  VStorageReadFileResult,
  VStorageSpecificCommandApi,
} from './type';
import {
  GET_DEFAULT_SPECIFIC_COMMAND_API,
  type RequestForExternalParameter,
  getPresignURLList,
  makeKey,
  requestForExternal,
  vStorageProgressPipe,
} from './util';

export default class TenantStorage<
  S extends VStorageSpecificCommandApi,
> implements IVStorage {
  static FILE_LOADING_MAP = {} as { [key: string]: boolean };

  private _bucket: string;
  private _config!: VStorageConfig;

  specificCommandApi!: S;

  constructor({
    config,
    specificCommandApi = GET_DEFAULT_SPECIFIC_COMMAND_API() as S,
  }: {
    config?: VStorageConfig;
    specificCommandApi?: S;
  }) {
    config && this.changeConfig(config);

    this.specificCommandApi = specificCommandApi;
    this._bucket = '';
  }

  get bucket() {
    return this._bucket;
  }

  get config() {
    return this._config;
  }

  get authHeaders() {
    const {
      idToken,
      tenant: { id },
    } = this.config;

    return {
      Authorization: `Bearer ${idToken}`,
      'X-Tenant-ID': id,
    };
  }

  get url() {
    const { endpoint } = this._config;

    return `${endpoint}`;
  }

  changeConfig(config: VStorageConfig): void {
    this._config = config;
  }

  request<T = any>({
    url,
    headers,
    ...rest
  }: Partial<RequestForExternalParameter>) {
    return requestForExternal<T>({
      url: url ?? this.url,
      ...rest,
      headers: {
        ...headers,
        'X-Bucket': this._bucket,
      },
    });
  }

  downloadDirectory(
    param: Omit<
      VStorageListCommandParameter,
      'maxKeys' | 'prefix' | 'startAfter'
    > & {
      key: string;
      downloadCallback?: (progress: VStorageProgress) => void;
    },
  ) {
    const { key, downloadCallback } = param;

    return this.specificCommandApi.downloadDirectory({
      key: makeKey(key, { isDir: true }).replace(/^\//, ''),
      downloadCallback,
    });
  }

  async createDirectory(param: VStorageGetCommandParameter) {
    const { key } = param;

    const [presignURL] = await getPresignURLList(this._bucket, [
      { key, option: { isDir: true } },
    ]);

    return this.request({
      method: 'PUT',
      url: presignURL!,
      headers: {
        'X-Tenant-ID': this.config.tenant.id!,
        'Content-Length': '0',
      },
    })
      .then(res => res.status === 201)
      .catch(err => {
        console.warn(err);
        return false;
      });
  }

  downloadFile(
    param: VStorageGetCommandParameter & {
      downloadCallback?: (progress: VStorageProgress) => void;
    },
  ) {
    const { key, downloadCallback } = param;

    return this.request<Blob>({
      /** @example `'/key'` */
      pathParams: makeKey(key).replace(/%[0-9a-zA-Z]/gi, token =>
        encodeURIComponent(token),
      ),
      responseType: 'blob',
      headers: this.authHeaders,
      onDownloadProgress: vStorageProgressPipe(downloadCallback),
    }).then(res => {
      const fileName = key.split('/').pop() ?? key;

      return {
        body: res.data,
        fileName,
      };
    });
  }

  readDirectory(param: VStorageListCommandParameter) {
    const { prefix, recursive, startAfter, maxKeys } = param;
    /** @example `'/prefix/'` */
    const _prefix = makeKey(prefix, { isDir: true });

    return this.specificCommandApi
      .getList({
        recursive: recursive ?? false,
        limit: maxKeys,
        prefix: _prefix.replace(/^\//, ''),
        startAfter,
      })
      .then(data => {
        const length = data.length;
        const fileList = [] as S3ObjectInfo[];
        const dirList = [] as S3ObjectInfo[];

        data.forEach(row => {
          if (row.key === _prefix) return;

          (row.etag ? fileList : dirList).push(row);
        });

        return {
          dirList,
          fileList,
          lastFile: data[length - 1],
        };
      });
  }

  async readDirectoryRecursive(
    param: Omit<VStorageListCommandParameter, 'startAfter'>,
  ) {
    const { prefix, recursive, maxKeys } = param;

    let startAfter: Nullish<string> = '';

    const dirList = [];
    const fileList = [];

    while (startAfter != null) {
      try {
        const res = await this.readDirectory({
          recursive,
          prefix,
          startAfter,
          maxKeys,
        });

        dirList.push(...res.dirList);
        fileList.push(...res.fileList);

        const lastKey = res.lastFile?.key;

        startAfter = lastKey !== startAfter ? lastKey : null;

        param.progressCallback?.({
          total: 0,
          rate: 0,
          loaded: dirList.length + fileList.length,
        });
      } catch (error) {
        console.warn(error);

        return Promise.reject(error);
      }
    }

    return {
      dirList,
      fileList,
    };
  }
  readFile(param: VStorageGetCommandParameter) {
    const { key, timestamp } = param;

    return this.request<ArrayBuffer>({
      /** @example `'/key'` */
      pathParams:
        makeKey(key) + `${timestamp ? `?timestamp=${Date.now()}` : ''}`,
      responseType: 'arraybuffer',
      headers: this.authHeaders,
    }).then(res => {
      const contentLength = res.headers['content-length'];

      return {
        key,
        body: res.data,
        contentLength: contentLength ? +contentLength : 0,
        contentType: res.headers['content-type'],
        eTag: res.headers['etag'],
        lastModified: new Date(res.headers['last-modified']),
      };
    });
  }
  async readFileList(
    param: Omit<VStorageGetCommandParameter, 'key'> & {
      keyList: string[];
      maxQueueSize?: number;
    },
  ) {
    const { keyList, maxQueueSize = 5, timestamp } = param;

    const queue = [] as Promise<any>[];
    const results = [] as VStorageReadFileResult[];

    TenantStorage.FILE_LOADING_MAP = {
      ...TenantStorage.FILE_LOADING_MAP,
      ...keyList.reduce(
        (acc, cur) => ({
          ...acc,
          [cur]: false,
        }),
        {} as Record<string, boolean>,
      ),
    };

    for (let i = 0; i < keyList.length; i++) {
      const key = makeKey(keyList[i]);

      if (TenantStorage.FILE_LOADING_MAP[key]) continue;
      if (queue.length >= maxQueueSize) await Promise.race(queue);

      await wait(100);

      TenantStorage.FILE_LOADING_MAP[key] = true;

      const node = this.readFile({ key, timestamp })
        .then(result => (results[i] = result))
        .catch(err => {
          console.group('Error readFiles index: [%d], key: [%s]', i, key);
          console.warn(err);
          console.groupEnd();

          results[i] = { key, body: null };
        })
        .finally(() => {
          queue.splice(queue.indexOf(node), 1);

          delete TenantStorage.FILE_LOADING_MAP[key];
        });

      queue.push(node);
    }

    await Promise.allSettled(queue);

    return results;
  }

  removeDirectory(
    param: Omit<
      VStorageListCommandParameter,
      'maxKeys' | 'path' | 'prefix' | 'startAfter'
    > & { key: string },
  ) {
    const { key } = param;

    return this.specificCommandApi.remove({
      key: makeKey(key, { isDir: true }),
    });
  }
  removeFile(param: { key: string }) {
    const { key } = param;

    return this.specificCommandApi.remove({ key: makeKey(key) });
  }

  selectBucket(bucket: string) {
    this._bucket = bucket;
    this.specificCommandApi.bucket = bucket;
  }

  uploadFile(
    param: VStoragePutCommandParameter & {
      progressCallback?: (progress: VStorageProgress) => void;
    },
  ): Promise<boolean> {
    const { presignURL, body, progressCallback } = param;

    return this.request({
      method: 'PUT',
      url: presignURL,
      headers: {
        'Content-Type': body!.type,
        'X-Tenant-ID': this.config.tenant.id!,
      },
      body,
      onUploadProgress: vStorageProgressPipe(progressCallback),
    })
      .then(res => res.status === 201)
      .catch(err => {
        console.warn(err);
        return false;
      });
  }
}
